import type { CSSProperties } from 'react';
import type {
  CreatedSampleRule,
  MarketingImportMergeNotice,
  SampleRule,
} from '@/infrastructure/FulfillmentAdminClient';
import { kstDay } from './fulfillmentTime';

/** Tokens are fixed by the wireframe spec order-management.md §3.9.2 (BR-2) — do not re-pick. */
export const MARKETING_ROW_TINT = '#F3EEFF';
export const MARKETING_ROW_HOVER_TINT = '#EBE1FF';
const MARKETING_BADGE_PURPLE = '#7C3AED';

/** Bootstrap paints cells with --bs-table-bg, so the row tint has to override the variable too. */
export const marketingRowStyle: CSSProperties & Record<'--bs-table-bg' | '--bs-table-hover-bg', string> = {
  backgroundColor: MARKETING_ROW_TINT,
  '--bs-table-bg': MARKETING_ROW_TINT,
  '--bs-table-hover-bg': MARKETING_ROW_HOVER_TINT,
};

/**
 * MKT is an order class, not a sourcing route — the purple has to carry that on its own.
 * Render it on a plain `span.badge`: react-bootstrap's Badge adds a `bg-*` class whose
 * `!important` background would paint over these tokens.
 */
export const marketingBadgeStyle: CSSProperties = {
  backgroundColor: MARKETING_BADGE_PURPLE,
  color: '#FFFFFF',
  fontWeight: 800,
  letterSpacing: '0.3px',
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Window bounds arrive either date-only or as a full instant; the operator reads a Seoul day either way.
 * A date-only bound is already a day, but an instant before 09:00 KST is still the previous UTC day.
 */
export const sampleWindowDay = (value?: string | null): string => {
  if (!value) {
    return 'forever';
  }
  return DATE_ONLY.test(value) ? value : kstDay(value);
};

/**
 * What the preview folded together, in words. The desk sees a row count on screen and an order count
 * afterwards, so the screen says which rows became which orders rather than leaving the gap unexplained.
 */
export const mergeNoticeText = (notice: MarketingImportMergeNotice): string =>
  `${notice.rows} ${notice.rows === 1 ? 'row' : 'rows'} will be created as ${notice.orders} ${notice.orders === 1 ? 'order' : 'orders'} — lines sharing a recipient and address are merged.`;

/** A window booked for later can be called off as surely as a running one; only a closed one cannot. */
export const isSampleWindowCancellable = (rule: Pick<SampleRule, 'status'>): boolean =>
  rule.status === 'ACTIVE' || rule.status === 'SCHEDULED';

/** The status as the desk reads it; the server sends it upper-case. */
export const sampleWindowStatusLabel = (status: string): string => {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'SCHEDULED':
      return 'Scheduled';
    case 'ENDED':
      return 'Ended';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
};

/** Running is green, booked is blue, and anything already closed recedes. */
export const sampleWindowStatusVariant = (status: string): string =>
  status === 'ACTIVE' ? 'success' : status === 'SCHEDULED' ? 'info' : 'secondary';

/**
 * The server keeps windows in the order they were opened and a window carries no creation time of its
 * own, so the newest is the last one it lists.
 */
export const newestSampleWindowsFirst = (rules: SampleRule[]): SampleRule[] => [...rules].reverse();

const orders = (count: number): string => `${count} ${count === 1 ? 'order' : 'orders'}`;

/** What opening a window did, as a headline and one line per order the set could not reach. */
export interface SampleWindowOutcome {
  /** Nothing went wrong: every named order now has a set, or none was named. */
  clean: boolean;
  summary: string;
  details: string[];
}

/**
 * Reads what the server said opening a window did. An all-new-orders window touches no order that
 * exists yet, so it reports zero targets now rather than leaving the desk to wonder whether it worked.
 */
export const describeSampleWindowOutcome = (created: CreatedSampleRule): SampleWindowOutcome => {
  if (created.applied === undefined && created.skipped === undefined && created.failed === undefined) {
    return {
      clean: true,
      summary: 'Assignment window opened — currently 0 target orders. It applies to orders created during the window.',
      details: [],
    };
  }
  const applied = created.applied ?? [];
  const skipped = created.skipped ?? [];
  const failed = created.failed ?? [];
  return {
    clean: skipped.length === 0 && failed.length === 0,
    summary: `Sample set applied to ${orders(applied.length)} · ${orders(skipped.length)} already had one · ${orders(failed.length)} not eligible`,
    details: [
      ...(skipped.length > 0 ? [`Already assigned: ${skipped.map((id) => `#${id}`).join(', ')}`] : []),
      ...failed.map((failure) => `#${failure.orderId}: ${failure.reason}`),
    ],
  };
};
